/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {getYesterday, isSame, today} from '@ion/core/src/utils/DateUtil';
import {TimeStamp} from './types';
  
/**
   * Categorizes notifications by their creation date into today, yesterday, and older.
   *
   * This function takes an array of notifications and groups them into three categories:
   * - `TimeStamp.TODAY`: Notifications created today.
   * - `TimeStamp.YESTERDAY`: Notifications created yesterday.
   * - `TimeStamp.OLDER`: Notifications created before yesterday.
   *
   * @param notifications - An array of notification objects. Each notification object is expected
   *                        to have a `createdOn` property representing the creation date.
   * @returns An object where keys are `TimeStamp` enum values and values are arrays of notifications
   *          falling under the respective time category.
   *
   * ```typescript
   * 
   * const notifications = [
   *   { createdOn: '2024-06-24T10:00:00Z', message: 'Notification 1' },
   *   { createdOn: '2024-06-25T12:00:00Z', message: 'Notification 2' },
   *   { createdOn: '2024-06-26T14:00:00Z', message: 'Notification 3' }
   * ];
   *
   * const categorizedNotifications = categorizeNotificationByDate(notifications);
   * console.log(categorizedNotifications);
   * // Output:
   * // {
   * //   'Today': [{ createdOn: '2024-06-26T14:00:00Z', message: 'Notification 3' }],
   * //   'Yesterday': [{ createdOn: '2024-06-25T12:00:00Z', message: 'Notification 2' }],
   * //   'Older': [{ createdOn: '2024-06-24T10:00:00Z', message: 'Notification 1' }]
   * // }
   * 
   * ```
   */
export const categorizeNotificationByDate = (notifications: any[]): Record<TimeStamp, Array<any>> => {
  return notifications?.reduce((acc, notification) => {
    const {createdOn} = notification;
    const isSameDay = isSame(createdOn, today().fromDate);
    const isYesterDay = isSame(createdOn, getYesterday().fromDate);
  
    const key = isSameDay ? TimeStamp.TODAY : isYesterDay ? TimeStamp.YESTERDAY : TimeStamp.OLDER;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(notification);
    return acc;
  }, {} as Record<TimeStamp, Array<any>>);
};
